import React, { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Phone, ChevronRight, Eye, ArrowRight, X } from "lucide-react";
import Navbar from "../components/landing/Navbar";
import WhatsAppButton from "../components/landing/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PC = {
  page_title:       "مخططات جازان",
  page_subtitle:    "أفضل المخططات السكنية في منطقة جازان بأسعار تنافسية ومواقع استراتيجية مميزة",
  region_badge:     "منطقة جازان",
  stat_plots_label: "مخطط",
  stat_region_label:"المنطقة",
  price_label:      "متوسط سعر المتر",
  total_plots_label:"إجمالي القطع",
  area_label:       "المساحة",
  contact_btn:      "تواصل معنا",
  view_pdf_btn:     "مشاهدة المخطط",
  cta_title:        "هل أنت مهتم بامتلاك قطعة أرض في جازان؟",
  cta_subtitle:     "تواصل معنا الآن للحصول على استشارة مجانية وأفضل الأسعار",
  cta_whatsapp_btn: "تواصل عبر واتساب",
  cta_form_btn:     "نموذج التواصل",
};

const STATUS_CFG = {
  "متاح":  { bg: "bg-green-500" },
  "محجوز": { bg: "bg-amber-500" },
  "مباع":  { bg: "bg-red-500" },
};
const FILTERS = ["الكل", "متاح", "محجوز", "مباع"];

const fullUrl = (url) => (!url ? null : url.startsWith("/api/") ? `${BACKEND}${url}` : url);

const PlotCard = ({ plot, wa, onViewPdf, pc }) => {
  const sc = STATUS_CFG[plot.status] || STATUS_CFG["متاح"];
  const pdf = fullUrl(plot.pdf_url);
  const waMsg = encodeURIComponent(`مرحباً، أود الاستفسار عن ${plot.name} في جازان`);
  const [imgIdx, setImgIdx] = useState(0);
  const imgs = (plot.images || []).map(fullUrl).filter(Boolean);
  const img = imgs[imgIdx] || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800&q=80";

  return (
    <div className="group bg-white rounded overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 flex flex-col">
      {/* Image */}
      <div className="relative h-60 overflow-hidden flex-shrink-0">
        <img src={img} alt={plot.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-[#0A1128]/20 to-transparent" />
        <span className={`absolute top-3 right-3 ${sc.bg} text-white text-xs font-black px-3 py-1 rounded-sm shadow`}>
          {plot.status || "متاح"}
        </span>
        {plot.badge && (
          <span className="absolute top-3 left-3 bg-[#ecc775] text-[#173269] text-xs font-black px-3 py-1 rounded-sm shadow">
            {plot.badge}
          </span>
        )}
        {imgs.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {imgs.map((_, i) => (
              <button key={i} onClick={() => setImgIdx(i)}
                className={`w-6 h-1.5 rounded-full transition-all duration-200 ${i === imgIdx ? "bg-[#ecc775]" : "bg-white/50"}`} />
            ))}
          </div>
        )}
        <div className="absolute bottom-6 right-4 left-4">
          <h3 className="text-white font-black text-xl drop-shadow-lg leading-tight">{plot.name}</h3>
          {plot.location && (
            <p className="text-white/75 text-sm flex items-center gap-1 mt-1">
              <MapPin size={12} className="text-[#ecc775]" />{plot.location}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        {/* Stats — 2 cols only (no available_plots) */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          {plot.total_plots && (
            <div className="text-center bg-[#173269]/5 rounded-sm p-2.5">
              <p className="font-black text-[#173269] text-xl leading-none">{plot.total_plots}</p>
              <p className="text-gray-500 text-xs mt-1">{pc.total_plots_label}</p>
            </div>
          )}
          {plot.plot_area && (
            <div className="text-center bg-[#173269]/5 rounded-sm p-2.5">
              <p className="font-black text-[#173269] text-base leading-none mt-0.5">{plot.plot_area}</p>
              <p className="text-gray-500 text-xs mt-1">{pc.area_label}</p>
            </div>
          )}
        </div>

        {/* Price */}
        {plot.price_from && (
          <div className="bg-gradient-to-r from-[#173269]/5 to-transparent border-r-4 border-[#ecc775] rounded-sm p-3 mb-4">
            <p className="text-gray-500 text-xs">{pc.price_label}</p>
            <p className="text-[#173269] font-black text-lg">{plot.price_from}</p>
          </div>
        )}

        {plot.description && (
          <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2 flex-1">{plot.description}</p>
        )}

        {/* Buttons */}
        <div className="flex gap-2 mt-auto">
          <a
            href={`https://wa.me/${wa}?text=${waMsg}`}
            target="_blank" rel="noopener noreferrer"
            data-testid={`plot-whatsapp-${plot.id}`}
            className="flex-1 bg-[#173269] text-white text-sm font-black py-3 rounded-sm text-center hover:bg-[#0A1128] transition-colors flex items-center justify-center gap-2"
          >
            <Phone size={14} />{pc.contact_btn}
          </a>
          {pdf && (
            <button
              onClick={() => onViewPdf(pdf, plot.name)}
              data-testid={`plot-pdf-${plot.id}`}
              className="flex-1 border-2 border-[#ecc775] text-[#173269] text-sm font-black py-3 rounded-sm text-center hover:bg-[#ecc775] transition-colors flex items-center justify-center gap-2"
            >
              <Eye size={14} />{pc.view_pdf_btn}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PlotsPage = () => {
  const [plots, setPlots]         = useState([]);
  const [filter, setFilter]       = useState("الكل");
  const [contact, setContact]     = useState({});
  const [pageContent, setContent] = useState({});
  const [loading, setLoading]     = useState(true);
  const [pdfModal, setPdfModal]   = useState({ open: false, url: "", title: "" });

  const pc = { ...DEFAULT_PC, ...pageContent };
  const openPdf  = (url, title) => setPdfModal({ open: true, url, title });
  const closePdf = () => setPdfModal({ open: false, url: "", title: "" });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/plots`),
      axios.get(`${API}/content/contact`),
      axios.get(`${API}/content/plots_page`),
    ])
      .then(([pr, cr, pcr]) => {
        setPlots(pr.data || []);
        setContact(cr.data.data || {});
        setContent(pcr.data.data || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.post(`${API}/track-visit`, { path: "/plots" }).catch(() => {});
  }, []);

  const filtered = filter === "الكل" ? plots : plots.filter(p => p.status === filter);
  const wa = contact?.whatsapp || "966555661937";
  const counts = {
    "الكل":  plots.length,
    "متاح":  plots.filter(p => p.status === "متاح").length,
    "محجوز": plots.filter(p => p.status === "محجوز").length,
    "مباع":  plots.filter(p => p.status === "مباع").length,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#173269]">
        <div className="w-14 h-14 border-4 border-[#ecc775] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="font-cairo min-h-screen bg-gray-50" dir="rtl">
      <Navbar contact={contact} />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-[#173269] via-[#1a3a7a] to-[#0A1128] pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 right-10 w-72 h-72 border border-[#ecc775]/10 rounded-full" />
          <div className="absolute bottom-0 left-20 w-48 h-48 border border-[#ecc775]/10 rotate-12" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
        </div>
        <div className="max-w-6xl mx-auto px-6 relative">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/50 text-sm mb-8">
            <a href="/" className="hover:text-[#ecc775] transition-colors flex items-center gap-1">
              <ArrowRight size={14} />الرئيسية
            </a>
            <ChevronRight size={13} className="rotate-180" />
            <span className="text-[#ecc775] font-semibold">{pc.page_title}</span>
          </div>

          <div className="flex flex-col lg:flex-row items-start justify-between gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-[#ecc775]/10 border border-[#ecc775]/20 rounded-sm px-4 py-2 mb-6">
                <MapPin size={14} className="text-[#ecc775]" />
                <span className="text-[#ecc775] text-sm font-bold">{pc.region_badge}</span>
              </div>
              <h1 className="text-white font-black text-5xl sm:text-6xl mb-5 leading-tight">{pc.page_title}</h1>
              <p className="text-white/65 text-lg max-w-xl leading-relaxed">{pc.page_subtitle}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 lg:flex-shrink-0">
              <div className="text-center bg-white/5 border border-white/10 rounded-sm p-5 backdrop-blur-sm">
                <p className="text-[#ecc775] font-black text-4xl">{plots.length}</p>
                <p className="text-white/60 text-xs mt-1 font-semibold">{pc.stat_plots_label}</p>
              </div>
              <div className="text-center bg-white/5 border border-white/10 rounded-sm p-5 backdrop-blur-sm">
                <p className="text-[#ecc775] font-black text-4xl">
                  {plots.reduce((a, p) => a + (Number(p.total_plots) || 0), 0)}
                </p>
                <p className="text-white/60 text-xs mt-1 font-semibold">{pc.total_plots_label}</p>
              </div>
              <div className="text-center bg-white/5 border border-white/10 rounded-sm p-5 backdrop-blur-sm">
                <p className="text-[#ecc775] font-black text-2xl">جازان</p>
                <p className="text-white/60 text-xs mt-1 font-semibold">{pc.stat_region_label}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Filter + Grid ── */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex gap-3 mb-10 flex-wrap">
          {FILTERS.map(f => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`px-6 py-2.5 rounded-sm font-bold text-sm border-2 transition-all duration-200 ${
                filter === f ? "bg-[#173269] text-white border-[#173269] shadow-lg shadow-[#173269]/20"
                             : "bg-white text-gray-600 border-gray-200 hover:border-[#173269]/40"}`}
            >
              {f}
              <span className={`mr-2 text-xs px-2 py-0.5 rounded-full font-black ${filter === f ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"}`}>
                {counts[f]}
              </span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-32">
            <div className="w-24 h-24 bg-[#173269]/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin size={44} className="text-[#173269]/20" />
            </div>
            <p className="text-gray-400 font-semibold text-xl">لا توجد مخططات في هذه الفئة</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((plot, i) => (
              <PlotCard key={plot.id || i} plot={plot} wa={wa} onViewPdf={openPdf} pc={pc} />
            ))}
          </div>
        )}
      </section>

      {/* ── CTA ── */}
      <section className="bg-gradient-to-br from-[#173269] to-[#0A1128] py-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <div className="absolute top-0 right-1/4 w-64 h-64 border border-[#ecc775] rounded-full -translate-y-1/2" />
        </div>
        <div className="max-w-3xl mx-auto text-center px-6 relative">
          <h2 className="text-white font-black text-4xl mb-5">{pc.cta_title}</h2>
          <p className="text-white/65 text-lg mb-10">{pc.cta_subtitle}</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
              className="bg-[#ecc775] text-[#173269] font-black px-10 py-4 rounded-sm hover:bg-white transition-colors shadow-xl shadow-[#ecc775]/20 text-base">
              {pc.cta_whatsapp_btn}
            </a>
            <a href="/#contact"
              className="border-2 border-[#ecc775]/50 text-[#ecc775] font-black px-10 py-4 rounded-sm hover:border-[#ecc775] hover:bg-[#ecc775]/10 transition-colors text-base">
              {pc.cta_form_btn}
            </a>
          </div>
        </div>
      </section>

      {/* ── PDF Modal ── */}
      {pdfModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget) closePdf(); }}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden" style={{ height: "90vh" }}>
            <div className="flex items-center justify-between px-5 py-3 bg-[#173269] flex-shrink-0">
              <div className="flex items-center gap-2">
                <Eye size={16} className="text-[#ecc775]" />
                <span className="text-white font-black text-sm">{pdfModal.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <a href={pdfModal.url} download
                  className="text-[#ecc775]/70 hover:text-[#ecc775] text-xs font-bold transition-colors px-3 py-1.5 border border-[#ecc775]/20 rounded-sm hover:border-[#ecc775]/50">
                  تحميل
                </a>
                <button onClick={closePdf}
                  className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-sm transition-colors">
                  <X size={18} />
                </button>
              </div>
            </div>
            <iframe src={pdfModal.url} title={pdfModal.title} className="flex-1 w-full border-0" />
          </div>
        </div>
      )}

      <WhatsAppButton whatsapp={wa} />
    </div>
  );
};

export default PlotsPage;
